import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, Camera, Battery, Image, Package, HelpCircle } from 'lucide-react'
import { useState } from 'react'

const AFFILIATE_LINK = 'https://www.amazon.com/dp/B0G834TQB4?linkCode=ll1&tag=homeinhouse-20&language=en_US&ref_=as_li_ss_tl'
const KODAK_AFFILIATE_LINK = 'https://www.amazon.com/KODAK-Recording-Simulation-Megapixels-Beginners/dp/B0FKBDT8FR?crid=X3F9LQ06EB1X&dib=eyJ2IjoiMSJ9.G0MDFabjJNmdTKtw8vbcOwhLAVtLbVcLh57r0dOUx2LvzeSxHR43Rv_OyxPqegZsdoKIfn-ygRQXQn5-LgtJKMMHzJsDDmmflLoLlWc1YDMq8v-sSAy9fp0qiHjo7R_T-4VVNYr_wRJuQhreifVl8VLHA4UY4xFjK_eaVOe9nOWcxVrokx1KaWZ65QgKqJSslvkaD9M7rWpghp4rpYCJB8Td6pprud4SGgrlnI69JmJsoYKG_fgsU1sT2L_juIlLnIRgPwXzf3-7yGotZU6GGc_M-xUXyqUThtyFS558YDs.6WELCPgeAOEMKNlg6RbVb0OiGwIUtFwAhWhPlO0f6Gg&dib_tag=se&keywords=kodak%2Bcamera&qid=1767863170&s=electronics&sprefix=kodak%2Bcharmer%2Celectronics%2C433&sr=1-1&th=1&linkCode=ll1&tag=homeinhouse-20&linkId=cad03399c5e08feaa378f5ab2278d26c&language=en_US&ref_=as_li_ss_tl'

const faqs = [
  {
    category: 'General',
    icon: Camera,
    questions: [
      {
        q: 'What is the G6 Thumb Camera?',
        a: 'The G6 Thumb Camera is an ultra-compact mini digital camera designed for everyday carry. Weighing only 26 grams, this tiny camera features a CCD sensor that produces authentic retro-style photos with warm, film-like colors. It\'s often called a "thumb camera" because of its incredibly small size — about the size of your thumb. The G6 is perfect for capturing candid moments, street photography, and anyone who wants a film point and shoot aesthetic without the hassle of film.'
      },
      {
        q: 'Is the G6 a film camera or digital camera?',
        a: 'The G6 is a digital camera, but it\'s designed to produce images with the look and feel of vintage film cameras. The CCD sensor technology (rather than modern CMOS sensors) naturally creates that warm, slightly nostalgic color science reminiscent of early 2000s point and shoot cameras. You get all the convenience of digital — instant viewing, no film costs, easy transfers — with the aesthetic of analog photography.'
      },
      {
        q: 'What makes the G6 different from smartphone cameras?',
        a: 'While smartphones excel at producing technically perfect, sharp images, the G6 thumb camera offers something different: character. The CCD sensor produces organic colors and gentle grain that can\'t be replicated with filters. The dedicated camera experience also makes you more intentional about photography. Many users find that having a separate mini digital camera leads to more creative, memorable photos because you\'re not distracted by notifications and apps.'
      },
      {
        q: 'Who is the G6 Thumb Camera for?',
        a: 'The G6 is perfect for: content creators wanting authentic retro aesthetics, travelers who need a lightweight backup camera, parents capturing everyday moments, students and young adults drawn to Y2K and vintage aesthetics, street photographers who value discretion, and anyone tired of over-processed smartphone photos. It\'s also popular as a gift for photography enthusiasts and nostalgia lovers.'
      }
    ]
  },
  {
    category: 'Features & Specs',
    icon: Package,
    questions: [
      {
        q: 'What resolution does the G6 camera shoot?',
        a: 'The G6 thumb camera shoots photos at 1080p HD resolution. While this may seem modest compared to modern smartphones, the resolution is intentional. Combined with the CCD sensor, it produces images with beautiful texture and character that higher resolutions often lose. The file sizes are also smaller, making it easy to share and store thousands of photos. For social media and prints up to 8x10", the quality is excellent.'
      },
      {
        q: 'How wide is the G6 camera lens?',
        a: 'The G6 features an impressive 130° wide-angle field of view. This ultra-wide perspective lets you capture expansive landscapes, fit more people into group shots, and create dynamic compositions. It\'s wider than most smartphone cameras, making this mini digital camera surprisingly versatile for its size.'
      },
      {
        q: 'What color modes are available?',
        a: 'The G6 comes with 8 built-in color profiles: Classic Film (warm vintage tones), Y2K Dream (punchy colors with lifted shadows), Noir Mode (true black and white), Summer Haze (dreamy warmth), Neon Nights (enhanced for low light), Pastel Soft (muted, gentle tones), High Contrast (bold and dramatic), and Natural (minimal processing). These profiles work directly in-camera — no editing required for that perfect retro mini camera look.'
      },
      {
        q: 'Does the G6 record video?',
        a: 'Yes! The G6 thumb camera records video at 1080p HD resolution. The videos have the same vintage character as the photos, with warm colors and organic motion. It\'s great for short clips, vlogs, and capturing moments where video tells the story better than a still image.'
      },
      {
        q: 'What colors does the G6 come in?',
        a: 'The G6 is available in 9 stunning colors: Yellow (the iconic retro-inspired shade), Blue, Red, Black, White, Yellow Green, Clear (translucent), Grey, and Green. Each color has the same features and performance, so you can choose based on your personal style. The Yellow variant is our most popular, embracing that classic film point and shoot aesthetic.'
      }
    ]
  },
  {
    category: 'Battery & Storage',
    icon: Battery,
    questions: [
      {
        q: 'How long does the G6 battery last?',
        a: 'The G6 thumb camera delivers approximately 200+ shots on a single charge — enough for a full day of casual shooting or an afternoon of intensive photography. The battery charges via USB-C, so you can top it up with the same cable you use for your phone. A full charge takes about 1-2 hours.'
      },
      {
        q: 'How are photos stored?',
        a: 'Photos are stored on a microSD card (not included). We recommend a 32GB or 64GB card, which can hold thousands of photos. The G6 supports cards up to 128GB. Transferring photos is easy — just pop the card into your computer or use a USB-C cable to connect the camera directly.'
      },
      {
        q: 'Can I transfer photos to my phone?',
        a: 'Yes! You can transfer photos from your G6 mini digital camera to your phone using a microSD card reader (available for both iPhone and Android) or by connecting the camera via USB-C to phones that support file transfer. Some users also use the microSD card in their computer and then cloud sync to their phone.'
      }
    ]
  },
  {
    category: 'Image Quality',
    icon: Image,
    questions: [
      {
        q: 'Why does the G6 use a CCD sensor instead of CMOS?',
        a: 'CCD (Charge-Coupled Device) sensors were used in vintage cameras from the 80s, 90s, and early 2000s. They produce colors differently than modern CMOS sensors — warmer, more organic, with a distinctive "bloom" in highlights. The G6 uses a modern CCD sensor specifically to capture that authentic retro aesthetic. It\'s not a gimmick or filter; it\'s genuine CCD color science that film point and shoot enthusiasts love.'
      },
      {
        q: 'Will photos look grainy?',
        a: 'The G6 produces a subtle, pleasing grain that adds character to images — especially in lower light. This is intentional and part of the vintage aesthetic. In good lighting conditions, images are clean and sharp. The grain is never harsh or distracting; it\'s more like the gentle texture you\'d see in film photography. Many users specifically choose this retro mini camera for that organic, imperfect look.'
      },
      {
        q: 'How does the G6 perform in low light?',
        a: 'The G6 performs admirably in low light for its size. The CCD sensor handles artificial lighting beautifully, producing warm tones and pleasant color casts. For very dark scenes, there will be some grain, but it\'s the attractive, film-like grain rather than ugly digital noise. The built-in Neon Nights mode is optimized for nighttime and indoor shooting.'
      }
    ]
  },
  {
    category: 'Purchasing & Support',
    icon: HelpCircle,
    questions: [
      {
        q: 'Where can I buy the G6 Thumb Camera?',
        a: 'The G6 thumb camera is available on Amazon with Prime shipping. Purchasing through our link supports our site and helps us continue creating content about mini digital cameras and retro photography. The G6 comes with a 30-day return policy through Amazon, so you can try it risk-free.'
      },
      {
        q: 'Does the G6 come with a warranty?',
        a: 'Yes, the G6 comes with a manufacturer warranty. Additionally, Amazon\'s return policy allows you to return or exchange the camera within 30 days if you\'re not completely satisfied. For any issues, Amazon\'s customer service is available to help.'
      },
      {
        q: 'What\'s included in the box?',
        a: 'The G6 thumb camera package includes: the G6 camera itself, a USB-C charging cable, a lanyard/wrist strap for carrying, and a quick start guide. Note that a microSD card is not included — we recommend picking up a 32GB or 64GB card for optimal storage.'
      },
      {
        q: 'Is the G6 good for kids?',
        a: 'Absolutely! The G6 mini digital camera is excellent for kids. Its small size fits perfectly in little hands, the simple controls are easy to learn, and the durability holds up to everyday use. Many parents buy the G6 as a first camera for children — it teaches photography fundamentals without the complexity (or cost) of more advanced cameras. The instant gratification of digital combined with the intentional shooting experience makes it educational and fun.'
      }
    ]
  }
]

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({})

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  return (
    <>
      <Helmet>
        <title>FAQ - G6 Thumb Camera Questions Answered | Mini Digital Camera Guide</title>
        <meta name="description" content="Frequently asked questions about the G6 Thumb Camera. Learn about CCD sensors, battery life, image quality, color modes, and why this mini digital camera is perfect for retro photography enthusiasts." />
        <link rel="canonical" href="https://g6optics.com/faq" />
        <meta property="og:title" content="G6 Thumb Camera FAQ - Your Questions Answered" />
        <meta property="og:description" content="Everything you need to know about the G6 mini digital camera. CCD sensor technology, vintage film colors, specs, and purchasing information." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://g6optics.com/faq" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="G6 Thumb Camera FAQ" />
        <meta name="twitter:description" content="Comprehensive FAQ about the G6 thumb camera, mini digital cameras, and retro photography." />
      </Helmet>

      <article className="seo-page faq-page">
        {/* Hero Section */}
        <header className="seo-hero">
          <div className="container">
            <h1>Frequently Asked Questions About the G6 Thumb Camera</h1>
            <p className="seo-hero-subtitle">
              Everything you need to know about this popular mini digital camera — from CCD sensor technology 
              to battery life, color modes, and why photographers are choosing the G6 for authentic 
              retro film camera aesthetics.
            </p>
          </div>
        </header>

        {/* Quick Jump Links */}
        <nav className="faq-nav" aria-label="FAQ Categories">
          <div className="container">
            <div className="faq-nav-links">
              {faqs.map((category, index) => (
                <a 
                  key={index} 
                  href={`#faq-${category.category.toLowerCase().replace(/\s+/g, '-')}`}
                  className="faq-nav-link"
                >
                  <category.icon size={18} />
                  {category.category}
                </a>
              ))}
            </div>
          </div>
        </nav>

        {/* FAQ Content */}
        <section className="faq-content">
          <div className="container">
            {faqs.map((category, categoryIndex) => (
              <div 
                key={categoryIndex} 
                className="faq-category"
                id={`faq-${category.category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <h2 className="faq-category-title">
                  <category.icon size={24} />
                  {category.category}
                </h2>
                
                <div className="faq-list">
                  {category.questions.map((item, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`
                    const isOpen = openItems[key]
                    
                    return (
                      <div 
                        key={questionIndex} 
                        className={`faq-item ${isOpen ? 'open' : ''}`}
                        itemScope 
                        itemProp="mainEntity" 
                        itemType="https://schema.org/Question"
                      >
                        <button
                          className="faq-question"
                          onClick={() => toggleItem(categoryIndex, questionIndex)}
                          aria-expanded={isOpen}
                        >
                          <h3 itemProp="name">{item.q}</h3>
                          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                        
                        <div 
                          className="faq-answer"
                          itemScope 
                          itemProp="acceptedAnswer" 
                          itemType="https://schema.org/Answer"
                          style={{ display: isOpen ? 'block' : 'none' }}
                        >
                          <p itemProp="text">{item.a}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="seo-cta">
          <div className="container">
            <h2>Ready to Experience the G6 Thumb Camera?</h2>
            <p>
              Join thousands of photographers who've discovered the joy of shooting with a mini digital camera 
              that captures moments with authentic character. The G6's CCD sensor, vintage color profiles, and 
              ultra-portable design make it the perfect companion for everyday creativity. Also looking at 
              the <a href={KODAK_AFFILIATE_LINK} target="_blank" rel="noopener noreferrer">Kodak</a>? Check out both options!
            </p>
            <div className="cta-buttons">
              <a 
                href={AFFILIATE_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Buy G6 on Amazon
              </a>
              <Link to="/g6-thumb-camera" className="btn btn-secondary">
                Explore Full Details
              </Link>
            </div>
          </div>
        </section>

        {/* SEO Internal Links */}
        <section className="seo-internal-links">
          <div className="container">
            <h2>Learn More About Mini Digital Cameras</h2>
            <div className="internal-links-grid">
              <Link to="/g6-thumb-camera" className="internal-link-card">
                <h3>G6 Thumb Camera</h3>
                <p>Complete product details, specifications, and color options for this popular tiny camera.</p>
              </Link>
              <Link to="/retro-mini-camera" className="internal-link-card">
                <h3>Retro Mini Cameras</h3>
                <p>Why CCD sensors create authentic vintage aesthetics and which cameras deliver true film-like results.</p>
              </Link>
              <Link to="/mini-digital-camera" className="internal-link-card">
                <h3>Mini Digital Cameras</h3>
                <p>The complete guide to compact digital cameras for travel, everyday carry, and creative photography.</p>
              </Link>
              <Link to="/" className="internal-link-card">
                <h3>G6 Optics Home</h3>
                <p>Discover our community, blog, and everything about retro digital camera photography.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Schema.org FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.flatMap(category => 
              category.questions.map(item => ({
                "@type": "Question",
                "name": item.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": item.a
                }
              }))
            )
          })}
        </script>
      </article>
    </>
  )
}
